import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Clock, X, Maximize2, Minimize2,
  Pause, Play, RotateCcw, Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Preaching Mode — fullscreen presentation view of a sermon draft.
 *
 * Slide sequence:
 *   1) Cover (topic / series / date / preacher)
 *   2..N+1) One slide per outline point (title + content, large readable type)
 *   N+2) Closing slide ("Amen" + total time)
 *
 * Controls:
 *   ← / →                navigate (Space / PageDown also advance)
 *   Esc                  exit
 *   T                    pause/resume the speaker timer
 *   R                    reset timer
 *   F                    toggle native fullscreen
 *
 * Touch: horizontal swipe (40px threshold) advances or rewinds.
 *
 * Speaker timer: starts when the FIRST non-cover slide is shown. Default
 * target 25 min, configurable via ?target_min= URL param. Past target the
 * timer turns red and pulses, but never auto-stops — pastors should know
 * they're over without the UI nagging them.
 *
 * Loads the draft via /api/sermons/drafts/{id} when ?draft_id= is present.
 * If location.state.draft is provided (in-page navigation from the editor)
 * we skip the network roundtrip so unsaved drafts can still rehearse.
 */
const SermonPresentMode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();

  const draftId = searchParams.get('draft_id');
  const targetMinutes = Math.max(
    1,
    Math.min(120, parseInt(searchParams.get('target_min') || '25', 10) || 25),
  );

  const [draft, setDraft] = useState(location.state?.draft || null);
  const [loading, setLoading] = useState(!location.state?.draft && !!draftId);
  const [error, setError] = useState(null);

  const [slideIdx, setSlideIdx] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [paused, setPaused] = useState(false);
  const [startedAt, setStartedAt] = useState(null);  // ms when timer first began
  const pausedElapsedRef = useRef(0);                // accumulated paused ms
  const lastPauseRef = useRef(null);                 // ms timestamp when paused
  const [isFullscreen, setIsFullscreen] = useState(false);

  const touchStartRef = useRef(null);

  /* ────────── load draft ────────── */
  useEffect(() => {
    if (draft || !draftId) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/sermons/drafts/${draftId}`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setDraft(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load draft');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ────────── tick ────────── */
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  /* ────────── derived: slides ────────── */
  const slides = useMemo(() => {
    if (!draft) return [];
    const list = [{ kind: 'cover' }];
    (draft.outline || []).forEach((p, i) => {
      list.push({ kind: 'point', point: p, idx: i + 1 });
    });
    list.push({ kind: 'closing' });
    return list;
  }, [draft]);
  const total = slides.length;
  const current = slides[slideIdx];

  /* ────────── timer logic ────────── */
  const targetMs = targetMinutes * 60 * 1000;

  const elapsedMs = useMemo(() => {
    if (!startedAt) return 0;
    if (paused && lastPauseRef.current) {
      return lastPauseRef.current - startedAt - pausedElapsedRef.current;
    }
    return now - startedAt - pausedElapsedRef.current;
  }, [startedAt, now, paused]);

  // Start the timer the FIRST time we leave the cover slide.
  useEffect(() => {
    if (slideIdx > 0 && !startedAt) {
      setStartedAt(Date.now());
    }
  }, [slideIdx, startedAt]);

  const handlePauseToggle = useCallback(() => {
    if (!startedAt) return;
    if (paused) {
      // resume → add the pause window to accumulator
      if (lastPauseRef.current) {
        pausedElapsedRef.current += Date.now() - lastPauseRef.current;
        lastPauseRef.current = null;
      }
      setPaused(false);
    } else {
      lastPauseRef.current = Date.now();
      setPaused(true);
    }
  }, [paused, startedAt]);

  const handleResetTimer = useCallback(() => {
    setStartedAt(slideIdx > 0 ? Date.now() : null);
    pausedElapsedRef.current = 0;
    lastPauseRef.current = null;
    setPaused(false);
  }, [slideIdx]);

  /* ────────── nav ────────── */
  const goPrev = useCallback(() => setSlideIdx((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setSlideIdx((i) => Math.min(total - 1, i + 1)),
    [total],
  );

  const exitMode = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    navigate(-1);
  }, [navigate]);

  /* ────────── fullscreen ────────── */
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  /* ────────── keyboard ────────── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') exitMode();
      else if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') goNext();
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') goPrev();
      else if (e.key === 't' || e.key === 'T') handlePauseToggle();
      else if (e.key === 'r' || e.key === 'R') handleResetTimer();
      else if (e.key === 'f' || e.key === 'F') toggleFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exitMode, goNext, goPrev, handlePauseToggle, handleResetTimer, toggleFullscreen]);

  /* ────────── touch ────────── */
  const onTouchStart = (e) => {
    touchStartRef.current = e.touches[0]?.clientX || null;
  };
  const onTouchEnd = (e) => {
    const start = touchStartRef.current;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX;
    if (end == null) return;
    const dx = end - start;
    if (Math.abs(dx) > 40) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartRef.current = null;
  };

  /* ────────── render ────────── */
  if (loading) {
    return (
      <div className="fixed inset-0 bg-zinc-950 text-white flex items-center justify-center z-50">
        <Loader2 className="w-8 h-8 animate-spin opacity-70" />
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="fixed inset-0 bg-zinc-950 text-white flex flex-col items-center justify-center z-50 gap-4">
        <p className="text-lg opacity-80">
          {error || (language === 'es' ? 'Borrador no encontrado' : 'Draft not found')}
        </p>
        <Button variant="outline" onClick={() => navigate('/sermon-prep')} data-testid="present-back-btn">
          {language === 'es' ? 'Volver al editor' : 'Back to editor'}
        </Button>
      </div>
    );
  }

  const fmtClock = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m}:${String(ss).padStart(2, '0')}`;
  };
  const overTime = elapsedMs > targetMs;
  const progressPct = total > 1 ? Math.round((slideIdx / (total - 1)) * 100) : 0;

  return (
    <div
      className="fixed inset-0 bg-zinc-950 text-zinc-100 flex flex-col z-50 select-none"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="preach-mode-root"
    >
      {/* Top bar */}
      <header className="flex items-center justify-between px-5 py-3 bg-zinc-900/60 backdrop-blur-sm border-b border-zinc-800">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            onClick={exitMode}
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-zinc-300 hover:text-white hover:bg-zinc-800"
            data-testid="preach-exit-btn"
          >
            <X className="w-4 h-4" />
          </Button>
          <div className="text-xs text-zinc-400 truncate">
            <span className="font-semibold text-zinc-200 truncate">
              {draft.topic || (language === 'es' ? 'Sermón sin título' : 'Untitled sermon')}
            </span>
            {draft.series_id && (
              <span className="ml-2 hidden sm:inline">
                · {draft.series_id}
                {draft.series_position != null && draft.series_position !== ''
                  ? ` · #${draft.series_position}`
                  : ''}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Speaker timer */}
          <div
            className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-mono tabular-nums border ${
              overTime
                ? 'bg-red-950/60 text-red-200 border-red-700 animate-pulse'
                : startedAt
                  ? 'bg-emerald-950/40 text-emerald-200 border-emerald-800'
                  : 'bg-zinc-800/60 text-zinc-400 border-zinc-700'
            }`}
            data-testid="preach-timer"
          >
            <Clock className="w-3.5 h-3.5" />
            <span>{fmtClock(elapsedMs)}</span>
            <span className="opacity-50">/ {targetMinutes}:00</span>
          </div>

          {/* Timer controls */}
          <Button
            onClick={handlePauseToggle}
            size="sm"
            variant="ghost"
            disabled={!startedAt}
            className="h-8 w-8 p-0 text-zinc-300 hover:text-white hover:bg-zinc-800 disabled:opacity-30"
            data-testid="preach-pause-btn"
            title={paused ? 'Resume (T)' : 'Pause (T)'}
          >
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </Button>
          <Button
            onClick={handleResetTimer}
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-zinc-300 hover:text-white hover:bg-zinc-800 hidden sm:flex"
            data-testid="preach-reset-btn"
            title="Reset (R)"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button
            onClick={toggleFullscreen}
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-zinc-300 hover:text-white hover:bg-zinc-800 hidden sm:flex"
            data-testid="preach-fullscreen-btn"
            title="Fullscreen (F)"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      {/* Progress strip */}
      <div className="h-1 bg-zinc-800">
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
          style={{ width: `${progressPct}%` }}
          data-testid="preach-progress"
        />
      </div>

      {/* Slide content */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 sm:px-12 py-8 overflow-auto">
        {current?.kind === 'cover' && (
          <div className="text-center max-w-4xl space-y-6" data-testid="preach-slide-cover">
            <p className="uppercase tracking-[0.4em] text-xs sm:text-sm text-purple-300/80">
              {language === 'es' ? 'Sermón' : 'Sermon'}
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05]">
              {draft.topic || (language === 'es' ? 'Sermón sin título' : 'Untitled sermon')}
            </h1>
            <div className="w-16 h-0.5 bg-purple-400/80 mx-auto" />
            <div className="text-base sm:text-lg text-zinc-400 space-y-1">
              {draft.series_id && (
                <p>
                  {language === 'es' ? 'Serie' : 'Series'}: {draft.series_id}
                  {draft.series_position != null && draft.series_position !== ''
                    ? ` · #${draft.series_position}`
                    : ''}
                </p>
              )}
              <p className="text-zinc-500 text-sm">
                {language === 'es'
                  ? `Toca o usa las flechas para avanzar · Objetivo ${targetMinutes} min`
                  : `Tap or use arrow keys to advance · Target ${targetMinutes} min`}
              </p>
            </div>
          </div>
        )}

        {current?.kind === 'point' && (
          <div className="max-w-4xl space-y-6 sm:space-y-8 w-full" data-testid={`preach-slide-${current.idx}`}>
            <div className="flex items-baseline gap-3 sm:gap-4">
              <span className="text-4xl sm:text-6xl font-black text-purple-400/80 tabular-nums">
                {current.idx}
              </span>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                {current.point.title || (language === 'es' ? 'Sin título' : 'Untitled')}
              </h2>
            </div>
            {current.point.content && (
              <p className="text-lg sm:text-2xl lg:text-3xl text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {current.point.content}
              </p>
            )}
          </div>
        )}

        {current?.kind === 'closing' && (
          <div className="text-center max-w-3xl space-y-6" data-testid="preach-slide-closing">
            <p className="text-7xl sm:text-9xl font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Amen
            </p>
            <p className="text-lg text-zinc-400">
              {language === 'es' ? 'Tiempo total' : 'Total time'}:{' '}
              <span className="font-mono text-white tabular-nums">{fmtClock(elapsedMs)}</span>
              {overTime && (
                <span className="ml-2 text-red-300">
                  ({language === 'es' ? 'sobre tiempo' : 'over target'})
                </span>
              )}
            </p>
          </div>
        )}
      </main>

      {/* Bottom bar */}
      <footer className="flex items-center justify-between px-4 sm:px-6 py-3 bg-zinc-900/60 backdrop-blur-sm border-t border-zinc-800">
        <Button
          onClick={goPrev}
          disabled={slideIdx === 0}
          variant="ghost"
          size="lg"
          className="text-zinc-300 hover:text-white hover:bg-zinc-800 disabled:opacity-30 h-12"
          data-testid="preach-prev-btn"
        >
          <ArrowLeft className="w-5 h-5 sm:mr-2" />
          <span className="hidden sm:inline">{language === 'es' ? 'Atrás' : 'Back'}</span>
        </Button>

        <div className="text-xs sm:text-sm text-zinc-400 tabular-nums" data-testid="preach-counter">
          {slideIdx + 1} / {total}
        </div>

        <Button
          onClick={goNext}
          disabled={slideIdx === total - 1}
          variant="ghost"
          size="lg"
          className="text-zinc-300 hover:text-white hover:bg-zinc-800 disabled:opacity-30 h-12"
          data-testid="preach-next-btn"
        >
          <span className="hidden sm:inline">{language === 'es' ? 'Siguiente' : 'Next'}</span>
          <ArrowRight className="w-5 h-5 sm:ml-2" />
        </Button>
      </footer>
    </div>
  );
};

export default SermonPresentMode;
